import { defineStore } from 'pinia'
import { markRaw } from 'vue'
import {
  getMonthKey,
  isIosDevice,
  isMobileDevice,
  isNativeApp,
  isRunningStandalone,
  readStoredValue,
  removeStoredValue,
  writeStoredValue,
  type IBeforeInstallPromptEvent,
} from '@/globals/typescript/utils/pwaInstall'

// "Install the app to your home screen" — the banner on the orders list and the card on the Mig
// page both read from here. Everything persisted is a per-DEVICE preference in localStorage (like
// appLockStore): an install belongs to a browser on a device, so Firestore would be wrong, and a
// user who installs on their phone should still be offered it on their laptop.
//
// Banner cadence, in one place: it is shown at most once per calendar month, on the first visit to
// the orders list after login that month, for as long as that app session lasts. Closing it with
// the X dismisses it permanently on this device; installing, or running as the installed app,
// ends it too. The Mig-page card is not subject to any of that — it is always there until the app
// is installed, as the way back for anyone who closed the banner.
const INSTALLED_KEY = 'pwaInstall:isInstalled'
const BANNER_DISMISSED_KEY = 'pwaInstall:isBannerDismissed'
const BANNER_LAST_SHOWN_MONTH_KEY = 'pwaInstall:bannerLastShownMonth'

export const usePwaInstallStore = defineStore('pwaInstall', {
  state: () => ({
    // Chromium hands over its install prompt once, via `beforeinstallprompt`. Kept raw: an event is
    // a platform object, and Vue's reactive Proxy around it makes `.prompt()` throw "Illegal invocation".
    deferredPrompt: null as IBeforeInstallPromptEvent | null,
    isStandalone: false,
    isInstalledOnDevice: readStoredValue(INSTALLED_KEY) === 'true',
    isBannerDismissed: readStoredValue(BANNER_DISMISSED_KEY) === 'true',
    bannerLastShownMonth: readStoredValue(BANNER_LAST_SHOWN_MONTH_KEY),
    // Once the banner has been shown this session it stays up for the rest of it, even though the
    // month is now recorded as "shown" — otherwise it would vanish the moment it appeared.
    isBannerActiveThisSession: false,
    isInitialised: false,
  }),

  getters: {
    /**
     * Whether this browser can offer an install right now: Chromium once it has fired
     * `beforeinstallprompt`, or any iOS device (which has no event, only manual steps).
     */
    isInstallable(state): boolean {
      if (isNativeApp() || state.isStandalone || state.isInstalledOnDevice) {
        return false
      }
      return state.deferredPrompt !== null || isIosDevice()
    },

    /** True when the install button can trigger the browser's own prompt rather than show iOS steps. */
    canPromptInstall: (state): boolean => state.deferredPrompt !== null,

    isBannerEligible(): boolean {
      return this.isInstallable && isMobileDevice() && !this.isBannerDismissed
    },

    shouldShowBanner(): boolean {
      if (!this.isBannerEligible) {
        return false
      }
      return this.isBannerActiveThisSession || this.bannerLastShownMonth !== getMonthKey()
    },
  },

  actions: {
    /**
     * Starts listening for install events. Called once from main.ts before anything awaits, because
     * `beforeinstallprompt` can fire as soon as the page is installable — earlier than first mount.
     */
    init(): void {
      if (this.isInitialised || isNativeApp()) {
        return
      }
      this.isInitialised = true

      this.isStandalone = isRunningStandalone()
      if (this.isStandalone) {
        // On Android the installed app shares localStorage with the browser, so this also stops the
        // banner in a normal Chrome tab. (On iOS the home-screen app has its own storage — harmless.)
        this.markInstalled()
      }

      window.addEventListener('beforeinstallprompt', (event) => {
        // Suppresses Chrome's own mini-infobar; our banner and Mig card are the install UI.
        event.preventDefault()
        this.deferredPrompt = markRaw(event as IBeforeInstallPromptEvent)
        // The event only fires while the app is not installed, so a stored "installed" flag is
        // stale — the app was uninstalled since.
        this.isInstalledOnDevice = false
        removeStoredValue(INSTALLED_KEY)
      })

      window.addEventListener('appinstalled', () => {
        this.deferredPrompt = null
        this.markInstalled()
      })

      window.matchMedia('(display-mode: standalone)').addEventListener('change', () => {
        this.isStandalone = isRunningStandalone()
        if (this.isStandalone) {
          this.markInstalled()
        }
      })
    },

    /**
     * Shows the browser's install prompt. 'unavailable' means there is no prompt to show (iOS, or
     * Chromium before it has offered one) and the caller should show manual steps instead.
     */
    async promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
      const event = this.deferredPrompt
      if (!event) {
        return 'unavailable'
      }

      // The event can be used once; Chromium fires a fresh one later if the user declines.
      this.deferredPrompt = null
      await event.prompt()
      const { outcome } = await event.userChoice
      if (outcome === 'accepted') {
        this.markInstalled()
      }
      return outcome
    },

    markInstalled(): void {
      this.isInstalledOnDevice = true
      writeStoredValue(INSTALLED_KEY, 'true')
    },

    /** Records that the banner is being shown, so it does not come back until next month. */
    markBannerShown(): void {
      this.isBannerActiveThisSession = true
      this.bannerLastShownMonth = getMonthKey()
      writeStoredValue(BANNER_LAST_SHOWN_MONTH_KEY, this.bannerLastShownMonth)
    },

    /** The banner's X: never show the banner on this device again. */
    dismissBanner(): void {
      this.isBannerDismissed = true
      this.isBannerActiveThisSession = false
      writeStoredValue(BANNER_DISMISSED_KEY, 'true')
    },
  },
})
